import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { fetchDashboardStats, fetchPendingReviewDocuments, triggerManualSync } from '@/store/slices/workAuthSlice';
import { workAuthService } from '@/services/workAuthService';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Users,
  Clock,
  FileCheck,
  AlertTriangle,
  TrendingUp,
  RefreshCw,
  Upload,
  Download,
  Calendar,
  Mail,
  FileSpreadsheet,
  ClipboardList,
  Search
} from 'lucide-react';
import type { WorkAuthResponse } from '@shared/schemas/workAuth';
import { formatDate } from '@/utils/dateFormatter';

const WorkAuthDashboardPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { stats, statsLoading, pendingReviewDocuments, syncStatus } = useAppSelector(state => state.workAuth);
  const [expiringEmployees, setExpiringEmployees] = useState<WorkAuthResponse[]>([]);
  const [pollingMailbox, setPollingMailbox] = useState(false);
  const [smartsheetSyncing, setSmartsheetSyncing] = useState(false);
  const [smartsheetSyncResult, setSmartsheetSyncResult] = useState<{
    success: boolean;
    newEmployeesCreated: number;
    employeesUpdated: number;
    errors: number;
  } | null>(null);

  // Search state
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<WorkAuthResponse[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  useEffect(() => {
    dispatch(fetchDashboardStats());
    dispatch(fetchPendingReviewDocuments({ status: 'PENDING', page: 1, limit: 5 }));
    loadExpiringEmployees();
  }, [dispatch]);

  // Debounced search effect
  useEffect(() => {
    if (!searchTerm.trim()) {
      setSearchResults([]);
      setSearchOpen(false);
      return;
    }

    const debounceTimer = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const response = await workAuthService.getWorkAuthorizations({
          search: searchTerm,
          page: 1,
          limit: 5
        });
        setSearchResults(response.data || []);
        setSearchOpen(true);
      } catch (error) {
        console.error('Search failed:', error);
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [searchTerm]);

  const loadExpiringEmployees = async () => {
    try {
      const response = await workAuthService.getEmployeesRequiringRenewal(90);
      setExpiringEmployees(response.data || []);
    } catch (error) {
      console.error('Failed to load expiring employees:', error);
    }
  };

  const handleManualSync = async () => {
    await dispatch(triggerManualSync());
    dispatch(fetchDashboardStats());
  };

  const handleCheckForUploads = async () => {
    setPollingMailbox(true);
    try {
      await workAuthService.pollMailbox();
      // Refresh dashboard data after polling
      dispatch(fetchDashboardStats());
      dispatch(fetchPendingReviewDocuments({ status: 'PENDING', page: 1, limit: 5 }));
      loadExpiringEmployees();
    } catch (error) {
      console.error('Failed to poll mailbox:', error);
    } finally {
      setPollingMailbox(false);
    }
  };

  const handleSmartsheetSync = async () => {
    setSmartsheetSyncing(true);
    setSmartsheetSyncResult(null);
    try {
      const response = await workAuthService.triggerSmartsheetSync();
      setSmartsheetSyncResult({
        success: response.data.success,
        newEmployeesCreated: response.data.newEmployeesCreated,
        employeesUpdated: response.data.employeesUpdated,
        errors: response.data.errors.length
      });
      // Refresh dashboard data after sync
      dispatch(fetchDashboardStats());
      dispatch(fetchPendingReviewDocuments({ status: 'PENDING', page: 1, limit: 5 }));
      loadExpiringEmployees();
    } catch (error) {
      console.error('Failed to sync from Smartsheet:', error);
      setSmartsheetSyncResult({
        success: false,
        newEmployeesCreated: 0,
        employeesUpdated: 0,
        errors: 1
      });
    } finally {
      setSmartsheetSyncing(false);
    }
  };

  const getUrgencyColor = (days: number) => {
    if (days <= 30) return 'text-red-600 bg-red-50 border-red-200';
    if (days <= 60) return 'text-yellow-600 bg-yellow-50 border-yellow-200';
    return 'text-blue-600 bg-blue-50 border-blue-200';
  };

  const getDaysUntilExpiration = (expirationDate: Date) => {
    const today = new Date();
    const expDate = new Date(expirationDate);
    const diffTime = expDate.getTime() - today.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const getInitials = (name: string) => {
    const parts = name.split(' ');
    return parts.map(p => p[0]).join('').toUpperCase().slice(0, 2);
  };

  const getTimelinePosition = (days: number) => {
    // Map 0-90 days to percentage position
    // 0 days = 0% (left/Today), 90 days = 100% (right/90d)
    const maxDays = 90;
    const position = (days / maxDays) * 100;
    return Math.max(0, Math.min(100, position));
  };

  if (statsLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 mb-8">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold text-foreground mb-2">Work Authorization Management</h1>
            <p className="text-muted-foreground">Monitor and manage employee work authorizations</p>
          </div>
          <Button onClick={handleManualSync} disabled={syncStatus?.isRunning}>
            <RefreshCw className={`h-4 w-4 mr-2 ${syncStatus?.isRunning ? 'animate-spin' : ''}`} />
            Run Notifications
          </Button>
        </div>

        {/* Search */}
        <div className="relative w-80">
          <Popover open={searchOpen && searchResults.length > 0} onOpenChange={setSearchOpen}>
            <PopoverTrigger asChild>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Search by name, email, or ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onFocus={() => searchResults.length > 0 && setSearchOpen(true)}
                  className="pl-10"
                />
                {searchLoading && (
                  <RefreshCw className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground animate-spin" />
                )}
              </div>
            </PopoverTrigger>
            <PopoverContent className="w-80 p-0" align="start">
              <div className="max-h-64 overflow-auto">
                {searchResults.map((result) => {
                  const days = getDaysUntilExpiration(result.expirationDate);
                  return (
                    <div
                      key={result.id}
                      className="flex items-center justify-between px-4 py-3 hover:bg-muted cursor-pointer border-b last:border-b-0"
                      onClick={() => {
                        navigate(`/hr/work-auth/${result.id}`);
                        setSearchTerm('');
                        setSearchOpen(false);
                      }}
                    >
                      <div>
                        <div className="font-medium">
                          {result.employee?.firstName} {result.employee?.lastName}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {result.employee?.employeeId} • {result.employee?.email}
                        </div>
                      </div>
                      <div className={`text-xs font-medium ${
                        days <= 30 ? 'text-red-600' : days <= 60 ? 'text-yellow-600' : 'text-green-600'
                      }`}>
                        {days > 0 ? `${days}d` : 'Expired'}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="border-t px-4 py-2">
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full text-xs"
                  onClick={() => {
                    navigate(`/hr/work-auth/list?search=${encodeURIComponent(searchTerm)}`);
                    setSearchOpen(false);
                  }}
                >
                  View all results →
                </Button>
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {/* Sync Status Alert */}
      {syncStatus && (
        <Alert>
          <Calendar className="h-4 w-4" />
          <AlertDescription>
            <div className="flex justify-between items-center">
              <span>
                Last sync: {new Date(syncStatus.lastRun).toLocaleString()} •
                Next scheduled: {new Date(syncStatus.nextRun).toLocaleString()}
              </span>
              {syncStatus.isRunning && (
                <span className="text-blue-600 font-medium">Sync in progress...</span>
              )}
            </div>
          </AlertDescription>
        </Alert>
      )}

      {/* Quick Actions */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Quick Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => navigate('/hr/work-auth/list')}>
              View All
            </Button>
            <Button variant="outline" onClick={() => navigate('/hr/work-auth/review')}>
              Review Documents
            </Button>
            <Button variant="outline" onClick={() => navigate('/hr/work-auth/emails')}>
              <Mail className="h-4 w-4 mr-2" />
              Incoming Emails
            </Button>
            <Button variant="outline" onClick={() => navigate('/hr/work-auth/smartsheet')}>
              <FileSpreadsheet className="h-4 w-4 mr-2" />
              View Smartsheet
            </Button>
            <Button variant="outline" onClick={() => navigate('/hr/work-auth/smartsheet/audit-logs')}>
              <ClipboardList className="h-4 w-4 mr-2" />
              Smartsheet Writes
            </Button>
            <Button
              variant="outline"
              onClick={handleCheckForUploads}
              disabled={pollingMailbox}
            >
              {pollingMailbox ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Mail className="h-4 w-4 mr-2" />
              )}
              Check Mailbox
            </Button>
            <Button
              variant="outline"
              onClick={handleSmartsheetSync}
              disabled={smartsheetSyncing}
            >
              {smartsheetSyncing ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-2" />
              )}
              Sync Smartsheet
            </Button>
          </div>
          {smartsheetSyncResult && (
            <div className={`text-xs mt-3 p-2 rounded ${smartsheetSyncResult.success ? 'bg-green-50 text-green-700 dark:bg-green-950 dark:text-green-300' : 'bg-red-50 text-red-700 dark:bg-red-950 dark:text-red-300'}`}>
              {smartsheetSyncResult.success ? (
                <>
                  Sync complete: {smartsheetSyncResult.newEmployeesCreated} created, {smartsheetSyncResult.employeesUpdated} updated
                  {smartsheetSyncResult.errors > 0 && `, ${smartsheetSyncResult.errors} errors`}
                </>
              ) : (
                'Sync failed. Check console for details.'
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
        {/* Total Active */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Active</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.totalActive || 0}</div>
            <p className="text-xs text-muted-foreground mt-1">Active work authorizations</p>
          </CardContent>
        </Card>

        {/* Pending Renewal */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pending Renewal</CardTitle>
            <Clock className="h-4 w-4 text-yellow-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-600">{stats?.pendingRenewal || 0}</div>
            <p className="text-xs text-muted-foreground mt-1">Awaiting document submission</p>
          </CardContent>
        </Card>

        {/* Pending Review */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pending Review</CardTitle>
            <FileCheck className="h-4 w-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">{stats?.documentsNeedingReview || 0}</div>
            <p className="text-xs text-muted-foreground mt-1">Documents awaiting HR review</p>
            {(stats?.documentsNeedingReview || 0) > 0 && (
              <Button
                size="sm"
                variant="link"
                className="p-0 h-auto mt-2"
                onClick={() => navigate('/hr/work-auth/review')}
              >
                Review now →
              </Button>
            )}
          </CardContent>
        </Card>

        {/* Expired */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Expired</CardTitle>
            <AlertTriangle className="h-4 w-4 text-red-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">{stats?.expired || 0}</div>
            <p className="text-xs text-muted-foreground mt-1">Expired authorizations</p>
          </CardContent>
        </Card>

        {/* No Notifications Sent */}
        <Card className={(stats?.expiringWithoutNotifications || 0) > 0 ? 'border-orange-300 bg-orange-50' : ''}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">No Notifications</CardTitle>
            <Mail className="h-4 w-4 text-orange-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">{stats?.expiringWithoutNotifications || 0}</div>
            <p className="text-xs text-muted-foreground mt-1">Expiring &lt;90d, no emails sent</p>
            {(stats?.expiringWithoutNotifications || 0) > 0 && (
              <Button
                size="sm"
                variant="link"
                className="p-0 h-auto mt-2 text-orange-700"
                onClick={() => navigate('/hr/work-auth/list?noNotifications=true')}
              >
                View all →
              </Button>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Expiring Soon Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* 30 Days */}
        <Card className={getUrgencyColor(30)}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              Expiring in 30 Days
            </CardTitle>
            <CardDescription>Critical - Immediate action required</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{stats?.expiringSoon.thirtyDays || 0}</div>
            <Button
              variant="link"
              className="p-0 h-auto mt-2 text-red-700 hover:text-red-800"
              onClick={() => navigate('/hr/work-auth/list?expiring=30')}
            >
              View all →
            </Button>
          </CardContent>
        </Card>

        {/* 60 Days */}
        <Card className={getUrgencyColor(60)}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Expiring in 60 Days
            </CardTitle>
            <CardDescription>Moderate - Action needed soon</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{stats?.expiringSoon.sixtyDays || 0}</div>
            <Button
              variant="link"
              className="p-0 h-auto mt-2 text-yellow-700 hover:text-yellow-800"
              onClick={() => navigate('/hr/work-auth/list?expiring=60')}
            >
              View all →
            </Button>
          </CardContent>
        </Card>

        {/* 90 Days */}
        <Card className={getUrgencyColor(90)}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Expiring in 90 Days
            </CardTitle>
            <CardDescription>Low - Monitor and track</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{stats?.expiringSoon.ninetyDays || 0}</div>
            <Button
              variant="link"
              className="p-0 h-auto mt-2 text-blue-700 hover:text-blue-800"
              onClick={() => navigate('/hr/work-auth/list?expiring=90')}
            >
              View all →
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Expiration Timeline */}
      {expiringEmployees.length > 0 && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="h-5 w-5" />
                  Expiration Timeline
                </CardTitle>
                <CardDescription>All employees plotted by document expiration date</CardDescription>
              </div>
              <div className="text-sm font-medium">{expiringEmployees.length} employees</div>
            </div>
          </CardHeader>
          <CardContent>
            {/* Timeline markers */}
            <div className="relative mb-16">
              {/* Timeline line */}
              <div className="absolute top-0 left-0 w-full h-1 bg-gray-200 rounded"></div>

              {/* Markers for Today, 30d, 60d, 90d */}
              <div className="relative flex justify-between text-xs text-muted-foreground">
                <div className="absolute left-0 -top-6">
                  <div className="w-0.5 h-4 bg-red-500 mx-auto mb-1"></div>
                  <div className="bg-red-500 text-white px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap">Today</div>
                </div>
                <div className="absolute left-1/3 -top-6 transform -translate-x-1/2">
                  <div className="w-0.5 h-4 bg-orange-500 mx-auto mb-1"></div>
                  <div className="bg-orange-500 text-white px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap">30d</div>
                </div>
                <div className="absolute left-2/3 -top-6 transform -translate-x-1/2">
                  <div className="w-0.5 h-4 bg-yellow-500 mx-auto mb-1"></div>
                  <div className="bg-yellow-500 text-white px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap">60d</div>
                </div>
                <div className="absolute right-0 -top-6">
                  <div className="w-0.5 h-4 bg-teal-500 mx-auto mb-1"></div>
                  <div className="bg-teal-500 text-white px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap">90d</div>
                </div>
              </div>

              {/* Employee markers */}
              <div className="relative mt-8">
                {expiringEmployees.map((emp) => {
                  const days = getDaysUntilExpiration(emp.expirationDate);
                  const position = getTimelinePosition(days);
                  const initials = getInitials((emp.employee?.firstName || '') + ' ' + (emp.employee?.lastName || ''));
                  const bgColor = days <= 30 ? 'bg-red-500' : days <= 60 ? 'bg-orange-500' : 'bg-teal-500';

                  return (
                    <div
                      key={emp.id}
                      className="absolute -top-1 transform -translate-x-1/2 group"
                      style={{ left: `${position}%` }}
                    >
                      <div
                        className={`w-10 h-10 ${bgColor} text-white rounded-full flex items-center justify-center text-xs font-bold cursor-pointer hover:scale-110 transition-transform`}
                        onClick={() => navigate(`/hr/work-auth/${emp.id}`)}
                      >
                        {initials}
                      </div>
                      {/* Tooltip */}
                      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                        <div className="bg-gray-900 text-white text-xs rounded-lg p-3 shadow-lg whitespace-nowrap">
                          <div className="font-semibold">{emp.employee?.firstName} {emp.employee?.lastName}</div>
                          <div className="text-gray-300 mt-1">ID: {emp.employee?.employeeId}</div>
                          <div className="text-gray-300">Type: {emp.documentType || 'N/A'}</div>
                          <div className="text-gray-300">Expires: {new Date(emp.expirationDate).toLocaleDateString()}</div>
                          <div className="font-semibold mt-1 text-yellow-400">{days} days remaining</div>
                          <div className="text-xs text-gray-400 mt-1">Click to view details</div>
                          {/* Arrow */}
                          <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-px">
                            <div className="border-4 border-transparent border-t-gray-900"></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Date range labels */}
            <div className="flex justify-between text-xs text-muted-foreground mt-4">
              <div>{new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</div>
              <div>Today</div>
              <div>{new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</div>
            </div>

            {/* Legend */}
            <div className="flex items-center gap-6 mt-6 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                <span>Critical (≤30 days)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-orange-500 rounded-full"></div>
                <span>Warning (≤60 days)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-teal-500 rounded-full"></div>
                <span>Good (&gt;60 days)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-gray-400 rounded-full"></div>
                <span>Expired</span>
              </div>
            </div>

            {/* Immediate Attention Required */}
            {expiringEmployees.filter(emp => getDaysUntilExpiration(emp.expirationDate) <= 30).length > 0 && (
              <div className="mt-6">
                <div className="flex items-center gap-2 text-red-600 font-semibold mb-3">
                  <AlertTriangle className="h-4 w-4" />
                  Immediate Attention Required
                </div>
                <div className="space-y-2">
                  {expiringEmployees
                    .filter(emp => getDaysUntilExpiration(emp.expirationDate) <= 30)
                    .map((emp) => {
                      const days = getDaysUntilExpiration(emp.expirationDate);
                      const initials = getInitials((emp.employee?.firstName || '') + ' ' + (emp.employee?.lastName || ''));
                      return (
                        <div
                          key={emp.id}
                          className="flex items-center justify-between p-4 bg-background border-2 border-red-300 dark:border-red-700 rounded-lg hover:border-red-500 hover:shadow-md transition-all cursor-pointer"
                          onClick={() => navigate(`/hr/work-auth/${emp.id}`)}
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-12 h-12 bg-red-500 text-white rounded-full flex items-center justify-center text-sm font-bold">
                              {initials}
                            </div>
                            <div>
                              <div className="font-semibold text-foreground">{emp.employee?.firstName} {emp.employee?.lastName}</div>
                              <div className="text-sm text-muted-foreground">{emp.employee?.employeeId}</div>
                              <div className="text-xs text-muted-foreground mt-0.5">Expires: {new Date(emp.expirationDate).toLocaleDateString()}</div>
                            </div>
                          </div>
                          <div className="bg-red-600 text-white px-4 py-2 rounded-full text-sm font-bold shadow-sm">
                            {days}d left
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Recent Documents Pending Review */}
      {pendingReviewDocuments && pendingReviewDocuments.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Recent Documents Pending Review</CardTitle>
            <CardDescription>Latest document submissions awaiting HR review</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {pendingReviewDocuments.slice(0, 5).map((doc) => (
                <div
                  key={doc.id}
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 cursor-pointer"
                  onClick={() => navigate('/hr/work-auth/review')}
                >
                  <div className="flex items-center gap-4">
                    <FileCheck className="h-8 w-8 text-blue-600" />
                    <div>
                      <p className="font-medium">{doc.fileName}</p>
                      <p className="text-sm text-muted-foreground">
                        Uploaded {formatDate(doc.submittedAt)} by {doc.submittedBy}
                      </p>
                    </div>
                  </div>
                  <Button variant="outline" size="sm">
                    Review
                  </Button>
                </div>
              ))}
            </div>
            {pendingReviewDocuments.length > 5 && (
              <Button
                variant="link"
                className="w-full mt-4"
                onClick={() => navigate('/hr/work-auth/review')}
              >
                View all {stats?.documentsNeedingReview} pending documents →
              </Button>
            )}
          </CardContent>
        </Card>
      )}

    </div>
  );
};

export default WorkAuthDashboardPage;